// Dashboard component - Refactored into smaller components
import React, { useMemo } from 'react';
import { Plus } from 'lucide-react';
import FinancialReports from './FinancialReports';

const DashboardAdminOverview = ({
    dashboardData,
    stats,
    serviceData,
    dateFilter,
    getTotalAmount,
    onServiceDataModalOpen,
    onExpenseListModalOpen,
    onCashIncomeModalOpen,
    setCashExpenseModalLoading,
    setShowActiveCustomersModal,
    setShowProfitModal,
    onSoldQuantityModalOpen,
    onCarSoldQuantityModalOpen,
    cashIncomeData,
    section
}) => {

    // Use cashExpenses from dashboardData with memoization
    const cashExpenses = useMemo(() => {
        return dashboardData.expenseData || [];
    }, [dashboardData.expenseData]);

    // Use cashIncomeData with memoization
    const cashIncomeFromManual = useMemo(() => {
        return cashIncomeData || [];
    }, [cashIncomeData]);

    if (section === 'sales') {
        return (
            <>
                {/* Satışlar Bölümü */}
                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="mb-6">
                        <h2 className="text-2xl font-black text-gray-900 mb-2 tracking-tight">SATIŞLAR</h2>
                        <p className="text-sm text-gray-600 font-medium">Satış performansı ve detayları</p>
                    </div>

                    {/* Ana Satış Kartları */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                        <div className="group relative overflow-hidden">
                            <div className="absolute inset-0 bg-primary-50 rounded-xl"></div>
                            <div className="relative p-5 bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:border-primary-300">
                                <div className="mb-3">
                                    <p className="text-sm font-bold text-gray-700 mb-2 tracking-wide">TOPLAM SATIŞ</p>
                                </div>
                                <p className="text-3xl font-black text-gray-900 mb-2 tracking-tight">
                                    {(dashboardData.salesSummary?.totalSalesAmount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺
                                </p>
                                <p className="text-xs text-gray-600 font-medium">Toplam gelir</p>
                            </div>
                        </div>

                        <div className="group relative overflow-hidden cursor-pointer" onClick={() => {
                            setShowProfitModal(true);
                        }}>
                            <div className="absolute inset-0 bg-primary-50 rounded-xl"></div>
                            <div className="relative p-5 bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:border-primary-300">
                                <div className="mb-3">
                                    <p className="text-sm font-bold text-gray-700 mb-2 tracking-wide">TOPLAM KAR</p>
                                </div>
                                <p className="text-3xl font-black text-gray-900 mb-2 tracking-tight">
                                    {(dashboardData.productProfits?.totalProfit || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺
                                </p>
                                <p className="text-xs text-gray-600 font-medium">
                                    {dashboardData.productProfits?.profitMargin > 0
                                        ? `Kar Marjı: +${dashboardData.productProfits.profitMargin.toFixed(1)}%`
                                        : 'Detaylar için tıklayın'}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Kasa Satışları */}
                        <div className="space-y-4">
                            <h4 className="text-lg font-black text-gray-900 tracking-wide">KASA SATIŞLARI</h4>

                            <div className="space-y-3">
                                <div className="p-4 bg-white rounded-xl border border-gray-200 hover:shadow-lg hover:border-primary-300 transition-all duration-300">
                                    <p className="text-sm font-bold text-gray-700 mb-2 tracking-wide">
                                        {(() => {
                                            const start = new Date(dateFilter?.startDate || new Date());
                                            const end = new Date(dateFilter?.endDate || new Date());
                                            const today = new Date();
                                            today.setHours(0, 0, 0, 0);

                                            // Tarih sadece gün bazında karşılaştır (saat dilimini göz ardı et)
                                            const startDay = new Date(start.getFullYear(), start.getMonth(), start.getDate());
                                            const endDay = new Date(end.getFullYear(), end.getMonth(), end.getDate());
                                            const todayDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());

                                            // Bu hafta başlangıç ve bitiş
                                            const startOfWeek = new Date(todayDay);
                                            startOfWeek.setDate(todayDay.getDate() - todayDay.getDay());
                                            const endOfWeek = new Date(startOfWeek);
                                            endOfWeek.setDate(startOfWeek.getDate() + 6);

                                            // Bu ay başlangıç ve bitiş
                                            const startOfMonth = new Date(todayDay.getFullYear(), todayDay.getMonth(), 1);
                                            const endOfMonth = new Date(todayDay.getFullYear(), todayDay.getMonth() + 1, 0);

                                            // Bugün kontrolü
                                            const isToday = startDay.getTime() === todayDay.getTime();

                                            // Bu hafta kontrolü (hafta Pazartesi başlar, Pazar biter)
                                            const isThisWeek = startDay.getTime() <= startOfWeek.getTime() && endDay.getTime() >= endOfWeek.getTime();

                                            // Bu ay kontrolü
                                            const isThisMonth = startDay.getTime() <= startOfMonth.getTime() && endDay.getTime() >= endOfMonth.getTime();

                                            let result;
                                            if (isToday) {
                                                result = 'BUGÜNKÜ SATIŞLAR';
                                            } else if (isThisWeek) {
                                                result = 'BU HAFTA SATIŞLAR';
                                            } else if (isThisMonth) {
                                                result = 'BU AY SATIŞLAR';
                                            } else {
                                                result = 'DÖNEM SATIŞLARI';
                                            }

                                            return result;
                                        })()}
                                    </p>
                                    <p className="text-2xl font-black text-gray-900 tracking-tight">
                                        {stats.sales.period.toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺
                                    </p>
                                </div>

                                <div className="p-4 bg-white rounded-xl border border-gray-200 cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:border-primary-300 transition-all duration-300 active:scale-[0.98]" onClick={onSoldQuantityModalOpen}>
                                    <p className="text-sm font-bold text-gray-700 mb-2 tracking-wide">SATILAN MIKTAR</p>
                                    <p className="text-2xl font-black text-gray-900 mb-1 tracking-tight">
                                        {stats.sales.quantity.toLocaleString('tr-TR')} adet
                                    </p>
                                    <p className="text-xs text-gray-600 font-medium">Detaylar için tıklayın</p>
                                </div>
                            </div>
                        </div>

                        {/* Araç Satışları */}
                        <div className="space-y-4">
                            <h4 className="text-lg font-black text-gray-900 tracking-wide">ARAÇ SATIŞLARI</h4>

                            <div className="space-y-3">
                                <div className="p-4 bg-white rounded-xl border border-gray-200 hover:shadow-lg hover:border-primary-300 transition-all duration-300">
                                    <p className="text-sm font-bold text-gray-700 mb-2 tracking-wide">ARAÇ SATIŞ GELİRİ</p>
                                    <p className="text-2xl font-black text-gray-900 tracking-tight">
                                        {(dashboardData.carSalesFlow?.income || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺
                                    </p>
                                </div>

                                <div className="p-4 bg-white rounded-xl border border-gray-200 cursor-pointer hover:shadow-lg hover:scale-[1.02] hover:border-primary-300 transition-all duration-300 active:scale-[0.98]" onClick={onCarSoldQuantityModalOpen}>
                                    <p className="text-sm font-bold text-gray-700 mb-2 tracking-wide">SATILAN MIKTAR</p>
                                    <p className="text-2xl font-black text-gray-900 mb-1 tracking-tight">
                                        {(dashboardData.carSalesFlow?.transactions || []).reduce((s, t) => s + (parseFloat(t.quantity) || 0), 0).toLocaleString('tr-TR')} adet
                                    </p>
                                    <p className="text-xs text-gray-600 font-medium">Detaylar için tıklayın</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (section === 'customers') {
        return (
            <>
                {/* Müşteri Bölümü */}
                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="mb-6">
                        <h2 className="text-2xl font-black text-gray-900 mb-2 tracking-tight">MÜŞTERİ ÖDEMELERİ</h2>
                        <p className="text-sm text-gray-600 font-medium">Müşteri ödemeleri ve istatistikler</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                        <div className="group relative overflow-hidden">
                            <div className="absolute inset-0 bg-primary-50 rounded-xl"></div>
                            <div className="relative p-5 bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:border-primary-300">
                                <p className="text-sm font-bold text-gray-700 mb-2 tracking-wide">MÜŞTERİ ÖDEMELERİ</p>
                                <p className="text-3xl font-black text-gray-900 mb-2 tracking-tight">
                                    {(dashboardData.dailyCashFlow.cashPaymentsIncome || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺
                                </p>
                                <p className="text-xs text-gray-600 font-medium">Toplam müşteri ödemeleri</p>
                            </div>
                        </div>

                        <div className="group relative overflow-hidden cursor-pointer" onClick={() => setShowActiveCustomersModal(true)}>
                            <div className="absolute inset-0 bg-primary-50 rounded-xl"></div>
                            <div className="relative p-5 bg-white rounded-xl border border-gray-200 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02] hover:border-primary-300">
                                <p className="text-sm font-bold text-gray-700 mb-2 tracking-wide">AKTİF MÜŞTERİ</p>
                                <p className="text-3xl font-black text-gray-900 mb-2 tracking-tight">
                                    {dashboardData.dailyCashFlow?.transactions?.length ||
                                        (dashboardData.dailyCashFlow?.cashPaymentsIncome > 0 ? 1 : 0)}
                                </p>
                                <p className="text-xs text-gray-600 font-medium">Müşteri ödemelerini görüntüle</p>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (section === 'cashflow') {
        return (
            <>
                {/* Nakit Yönetimi */}
                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                    <div className="mb-6">
                        <h2 className="text-2xl font-black text-gray-900 mb-2 tracking-tight">NAKİT YÖNETİMİ</h2>
                    </div>

                    <div className="flex flex-wrap gap-3 mb-6">
                        <button
                            onClick={onServiceDataModalOpen}
                            className="flex items-center px-4 py-2 bg-primary-600 text-white rounded-xl hover:bg-primary-700 text-sm font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Servis Verisi
                        </button>
                        <button
                            onClick={onCashIncomeModalOpen}
                            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-xl hover:bg-blue-700 text-sm font-semibold shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 active:scale-95"
                        >
                            <Plus className="w-4 h-4 mr-2" />
                            Kasa İşlemleri
                        </button>
                    </div>

                    {/* Ana Nakit Akış Tablosu */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
                        {/* Servis Bölümü */}
                        <div className="relative overflow-hidden">
                            <div className="relative bg-white rounded-2xl border border-gray-200 p-5 shadow-lg hover:shadow-xl transition-all duration-300">
                                <h4 className="text-lg font-black text-gray-900 mb-5 tracking-wide">SERVİS</h4>

                                {/* Gelen */}
                                <div className="mb-5">
                                    <h5 className="text-sm font-bold text-gray-700 mb-3 tracking-wide">GELİR</h5>
                                    <div className="space-y-2.5">
                                        {(serviceData.income || []).map((item, index) => (
                                            <div key={item.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
                                                <span className="text-sm font-medium text-gray-700">{item.name || `Gelir ${index + 1}`}</span>
                                                <span className="font-bold text-green-600">{item.amount.toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺</span>
                                            </div>
                                        ))}
                                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl border-2 border-green-200 shadow-sm">
                                            <span className="text-sm font-bold text-gray-800">TOPLAM SERVİS GELİRİ</span>
                                            <span className="font-bold text-lg text-green-600">{getTotalAmount(serviceData.income || []).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Giden */}
                                <div>
                                    <h5 className="text-sm font-bold text-gray-700 mb-3 tracking-wide">GİDER</h5>
                                    <div className="space-y-2.5">
                                        {(serviceData.expenses || []).map((item, index) => (
                                            <div key={item.id} className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
                                                <span className="text-sm font-medium text-gray-700">{item.name || `Gider ${index + 1}`}</span>
                                                <span className="font-bold text-red-600">-{item.amount.toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺</span>
                                            </div>
                                        ))}
                                        <div className="flex items-center justify-between p-3 bg-red-50 rounded-xl border-2 border-red-200 shadow-sm">
                                            <span className="text-sm font-bold text-gray-800">SERVİS GİDERİ</span>
                                            <span className="font-bold text-lg text-red-600">-{getTotalAmount(serviceData.expenses || []).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Kasa Bölümü */}
                        <div className="relative overflow-hidden">
                            <div className="relative bg-white rounded-2xl border border-gray-200 p-5 shadow-lg hover:shadow-xl transition-all duration-300">
                                <h4 className="text-lg font-black text-gray-900 mb-5 tracking-wide">KASA</h4>

                                {/* Kasa Gelen */}
                                <div className="mb-5">
                                    <h5 className="text-sm font-bold text-gray-700 mb-3 tracking-wide">KASA GELİR</h5>
                                    <div className="space-y-2.5">
                                        <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
                                            <span className="text-sm font-medium text-gray-700">Müşteri Ödemeleri</span>
                                            <span className="font-bold text-green-600">{(dashboardData.dailyCashFlow.cashPaymentsIncome || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺</span>
                                        </div>
                                        <div className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
                                            <span className="text-sm font-medium text-gray-700">Satış Geliri</span>
                                            <span className="font-bold text-green-600">{(dashboardData.dailyCashFlow.cashSalesIncome || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺</span>
                                        </div>
                                        {cashIncomeFromManual.map((item, index) => (
                                            <div key={item.id || index} className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
                                                <span className="text-sm font-medium text-gray-700 truncate">
                                                    {item.description || `Manuel Gelir ${index + 1}`}
                                                </span>
                                                <span className="font-bold text-green-600">
                                                    {Number(item.amount).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺
                                                </span>
                                            </div>
                                        ))}
                                        <div className="flex items-center justify-between p-3 bg-green-50 rounded-xl border-2 border-green-200 shadow-sm">
                                            <span className="text-sm font-bold text-gray-800">TOPLAM KASA GELİRİ</span>
                                            <span className="font-bold text-lg text-green-600">{(dashboardData.dailyCashFlow.cashIncome || 0).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Kasa Giden */}
                                <div>
                                    <h5 className="text-sm font-bold text-gray-700 mb-3 tracking-wide">KASA GİDER</h5>
                                    <div className="space-y-2.5">
                                        {cashExpenses.length > 0 ? (
                                            <>
                                                <div className="space-y-2 max-h-40 overflow-y-auto pr-1">
                                                    {cashExpenses.map((item, index) => (
                                                        <div key={item.id || index} className="flex items-center justify-between p-3 bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-all duration-200">
                                                            <span className="text-sm font-medium text-gray-700 truncate">
                                                                {item.description || `Gider ${index + 1}`}
                                                            </span>
                                                            <span className="font-bold text-red-600">
                                                                -{Number(item.amount).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                                <div className="flex items-center justify-between p-3 bg-red-50 rounded-xl border-2 border-red-200 shadow-sm">
                                                    <span className="text-sm font-bold text-gray-800">KASA GİDERİ</span>
                                                    <span className="font-bold text-lg text-red-600">-{getTotalAmount(cashExpenses).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺</span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="flex items-center justify-between p-3 bg-red-50 rounded-xl border-2 border-red-200 shadow-sm">
                                                <span className="text-sm font-bold text-gray-800">TOPLAM KASA GİDEN</span>
                                                <span className="font-bold text-lg text-red-600">-{getTotalAmount(cashExpenses).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Özet Bölümü */}
                    <div className="relative overflow-hidden">
                        <div className="absolute inset-0 bg-gray-50 rounded-2xl"></div>
                        <div className="relative p-5 bg-white rounded-2xl border border-gray-200 shadow-lg">
                            <h4 className="text-lg font-black text-gray-900 mb-4 tracking-wide">FİNANSAL ÖZET</h4>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                <div className="group relative overflow-hidden">
                                    <div className="absolute inset-0 bg-green-50"></div>
                                    <div className="relative p-4 bg-white rounded-xl border border-green-200 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                                        <div className="text-center">
                                            <div className="text-2xl font-black text-gray-900 mb-1 tracking-tight">
                                                {dashboardData.dailyCashFlow.income.toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺
                                            </div>
                                            <div className="text-sm font-bold text-gray-700 tracking-wide">TOPLAM GELİR</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="group relative overflow-hidden">
                                    <div className="absolute inset-0 bg-red-50"></div>
                                    <div className="relative p-4 bg-white rounded-xl border border-red-200 shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02]">
                                        <div className="text-center">
                                            <div className="text-2xl font-black text-gray-900 mb-1 tracking-tight">
                                                {dashboardData.dailyCashFlow.expenses.toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺
                                            </div>
                                            <div className="text-sm font-bold text-gray-700 tracking-wide">TOPLAM GİDER</div>
                                        </div>
                                    </div>
                                </div>
                                <div className="group relative overflow-hidden">
                                    <div className={`absolute inset-0 ${dashboardData.dailyCashFlow.netFlow >= 0
                                            ? 'bg-green-50'
                                            : 'bg-red-50'
                                        }`}></div>
                                    <div className={`relative p-4 bg-white rounded-xl border shadow-md hover:shadow-lg transition-all duration-300 hover:scale-[1.02] ${dashboardData.dailyCashFlow.netFlow >= 0
                                            ? 'border-green-200'
                                            : 'border-red-200'
                                        }`}>
                                        <div className="text-center">
                                            <div className={`text-2xl font-black mb-1 tracking-tight ${dashboardData.dailyCashFlow.netFlow >= 0
                                                    ? 'text-green-600'
                                                    : 'text-red-600'
                                                }`}>
                                                {dashboardData.dailyCashFlow.netFlow.toLocaleString('tr-TR', { minimumFractionDigits: 0 })} ₺
                                            </div>
                                            <div className="text-sm font-bold text-gray-700 tracking-wide">NET DURUM</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    if (section === 'financial-summary') {
        return <FinancialReports dashboardData={dashboardData} dateFilter={dateFilter} />;
    }

    return null;
};

export default DashboardAdminOverview;
